(function () {
  let playerPromise = null;

  async function inflate(bytes) {
    const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  }

  function readZip(buffer) {
    const view = new DataView(buffer);
    let eocd = buffer.byteLength - 22;
    while (eocd >= 0 && view.getUint32(eocd, true) !== 0x06054b50) eocd--;
    if (eocd < 0) throw new Error('kp-ingredientes: zip inválido');
    const count = view.getUint16(eocd + 10, true);
    const decoder = new TextDecoder();
    const files = {};
    let pos = view.getUint32(eocd + 16, true);
    for (let i = 0; i < count; i++) {
      const method = view.getUint16(pos + 10, true);
      const size = view.getUint32(pos + 20, true);
      const nameLength = view.getUint16(pos + 28, true);
      const extraLength = view.getUint16(pos + 30, true);
      const commentLength = view.getUint16(pos + 32, true);
      const local = view.getUint32(pos + 42, true);
      const name = decoder.decode(new Uint8Array(buffer, pos + 46, nameLength));
      pos += 46 + nameLength + extraLength + commentLength;
      if (name.endsWith('/') || name.startsWith('__MACOSX/')) continue;
      const start = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true);
      const data = new Uint8Array(buffer, start, size);
      files[name] = () => (method === 8 ? inflate(data) : Promise.resolve(data));
    }
    return files;
  }

  function toBase64(bytes) {
    let binary = '';
    for (let i = 0; i < bytes.length; i += 0x8000) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
    }
    return btoa(binary);
  }

  async function animationFromZip(buffer) {
    const files = readZip(buffer);
    const names = Object.keys(files);
    const text = async (name) => new TextDecoder().decode(await files[name]());
    let jsonName = null;
    if (files['manifest.json']) {
      const manifest = JSON.parse(await text('manifest.json'));
      const id = manifest.animations && manifest.animations[0] && manifest.animations[0].id;
      if (id) jsonName = names.find((name) => name === `animations/${id}.json` || name === `a/${id}.json`);
    }
    if (!jsonName) jsonName = names.find((name) => /\.json$/i.test(name) && !/(^|\/)manifest\.json$/i.test(name));
    if (!jsonName) throw new Error('kp-ingredientes: nenhum .json do Lottie no zip');
    const data = JSON.parse(await text(jsonName));

    await Promise.all(
      (data.assets || []).map(async (asset) => {
        if (!asset.p || asset.e === 1 || asset.p.startsWith('data:')) return;
        const base = asset.p.split('/').pop();
        const name =
          names.find((n) => n === (asset.u || '') + asset.p) || names.find((n) => n.split('/').pop() === base);
        if (!name) return;
        const ext = base.split('.').pop().toLowerCase();
        const type = ext === 'svg' ? 'image/svg+xml' : ext === 'jpg' ? 'image/jpeg' : `image/${ext}`;
        asset.p = `data:${type};base64,${toBase64(await files[name]())}`;
        asset.u = '';
        asset.e = 1;
      })
    );
    return data;
  }

  function lastKeyframe(node) {
    let last = 0;
    if (Array.isArray(node)) {
      node.forEach((child) => (last = Math.max(last, lastKeyframe(child))));
    } else if (node && typeof node === 'object') {
      if (node.a === 1 && Array.isArray(node.k)) {
        node.k.forEach((key) => {
          if (key && typeof key.t === 'number') last = Math.max(last, key.t);
        });
      }
      Object.keys(node).forEach((key) => (last = Math.max(last, lastKeyframe(node[key]))));
    }
    return last;
  }

  async function loadAnimationData(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`kp-ingredientes: ${response.status} ao baixar ${url}`);
    const buffer = await response.arrayBuffer();
    const bytes = new Uint8Array(buffer, 0, 4);
    const isZip = bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;
    return isZip ? animationFromZip(buffer) : JSON.parse(new TextDecoder().decode(buffer));
  }

  function loadPlayer(src) {
    if (window.lottie) return Promise.resolve(window.lottie);
    if (!playerPromise) {
      playerPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve(window.lottie);
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    return playerPromise;
  }

  function start(section) {
    const boxes = Array.from(section.querySelectorAll('[data-kp-lottie]'));
    if (!boxes.length) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const play = () =>
      loadPlayer(section.dataset.kpLottieSrc).then((lottie) => {
        boxes.forEach((box) => {
          if (box.dataset.kpLottieReady) return;
          box.dataset.kpLottieReady = 'true';
          loadAnimationData(box.dataset.kpLottie)
            .then((animationData) => {
              const end = lastKeyframe(animationData.layers.concat(animationData.assets || []));
              const anim = lottie.loadAnimation({
                container: box,
                renderer: 'svg',
                loop: !reduce,
                autoplay: !reduce,
                animationData,
                initialSegment: end > animationData.ip && end < animationData.op ? [animationData.ip, end] : undefined,
                rendererSettings: { preserveAspectRatio: 'xMidYMax meet' },
              });
              if (reduce) anim.addEventListener('DOMLoaded', () => anim.goToAndStop(0, true));
            })
            .catch((error) => console.error(error));
        });
      });

    if (!('IntersectionObserver' in window)) {
      play();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          play();
        }
      },
      { rootMargin: '300px 0px' }
    );
    observer.observe(section);
  }

  function init() {
    document.querySelectorAll('.kp-ingredientes[data-kp-lottie-src]').forEach(start);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.kp-ingredientes[data-kp-lottie-src]');
    if (section) start(section);
  });
})();